import * as THREE from 'three/webgpu'
import { FEATURE_LINE_PICK_RADIUS_PX } from '../../constants/solidworks-picking'
import { resolveScreenSpacePointPickRadiusPx } from '../../manager/pick/utils/screenSpacePickPolicy'
import type { FeatureResultMesh } from '../../mesh/feature'
import { SW_VERTEX_PICK_THRESHOLD_PX } from '../render/sw-theme'

/** 默认最多保留的身份/世界矩阵投影上下文数量。 */
const DEFAULT_MAX_CACHED_PROJECTION_CONTEXTS = 128
/** 默认最多保留的世界/屏幕投影点数量。 */
const DEFAULT_MAX_CACHED_PROJECTED_POINTS = 65_536
/** 采样边缓存中每个点依次保存世界 XYZ 与客户端 XY。 */
const PROJECTED_POINT_STRIDE = 5
/** 只用于合并矩阵运算产生的不可见浮点抖动。 */
const PROJECTION_MATRIX_EQUALITY_EPSILON = 1e-12

/**
 * 导入拓扑快照屏幕空间拾取器。
 *
 * 职责：
 * 1. 直接读取当前命中面的权威拓扑快照完成点、边精筛；
 * 2. 在确定最终索引前不创建 Three.js 点线代理；
 * 3. 复用全部热路径临时对象，避免密集孔位悬停产生代理风暴和垃圾回收压力。
 */
export class ImportedModelTopologySnapshotPicker {
  /** 与旧 FeaturePoint 临时缩放链完全一致的点拾取半径。 */
  private readonly vertexPickRadiusPx = resolveScreenSpacePointPickRadiusPx(
    true,
    SW_VERTEX_PICK_THRESHOLD_PX / 1000,
  )

  /** 当前面集合去重后的候选边索引。 */
  private readonly edgeIndices = new Set<number>()

  /** 当前面集合去重后的候选顶点索引。 */
  private readonly vertexIndices = new Set<number>()

  /** 当前候选世界坐标。 */
  private readonly worldPoint = new THREE.Vector3()

  /** 当前候选投影坐标。 */
  private readonly projectedPoint = new THREE.Vector3()

  /** 直线边两个端点的扁平投影缓冲。 */
  private readonly straightEdgePointData = new Float64Array(PROJECTED_POINT_STRIDE * 2)

  /** 射线方向距离计算缓存。 */
  private readonly rayOffset = new THREE.Vector3()

  /** 当前最佳顶点命中世界坐标。 */
  private readonly bestVertexWorldPoint = new THREE.Vector3()

  /** 当前最佳边命中世界坐标。 */
  private readonly bestEdgeWorldPoint = new THREE.Vector3()

  /** 按最近使用顺序保存的身份/矩阵投影上下文。 */
  private readonly projectionContexts = new Map<
    ImportedModelTopologyProjectionHostContext,
    ImportedModelTopologyProjectionHostContext
  >()

  /** 同一 source 或实例槽身份可对应多个阵列世界矩阵变体。 */
  private projectionContextsByIdentity = new WeakMap<
    object,
    ImportedModelTopologyProjectionHostContext[]
  >()

  /** 相机投影矩阵副本。 */
  private readonly cameraProjectionMatrix = new THREE.Matrix4()

  /** 相机世界逆矩阵副本。 */
  private readonly cameraWorldInverseMatrix = new THREE.Matrix4()

  /** 相机和视口快照是否已初始化。 */
  private viewportInitialized = false

  /** 上次视口左坐标。 */
  private viewportLeft = 0

  /** 上次视口上坐标。 */
  private viewportTop = 0

  /** 上次视口宽度。 */
  private viewportWidth = 0

  /** 上次视口高度。 */
  private viewportHeight = 0

  /** 身份/矩阵上下文数量上限。 */
  private readonly maxCachedContexts: number

  /** 投影点总数上限。 */
  private readonly maxCachedProjectedPoints: number

  /** 当前全部宿主缓存的投影点数。 */
  private cachedProjectedPoints = 0

  /** 相机或视口全局刷新次数。 */
  private viewportRefreshes = 0

  /** 宿主投影上下文刷新次数。 */
  private contextRefreshes = 0

  /** 宿主投影上下文命中次数。 */
  private contextHits = 0

  /** 宿主投影上下文预算淘汰次数。 */
  private contextEvictions = 0

  /** 顶点投影构建次数。 */
  private vertexProjectionBuilds = 0

  /** 顶点投影缓存命中次数。 */
  private vertexProjectionHits = 0

  /** 采样边投影构建次数。 */
  private sampledEdgeProjectionBuilds = 0

  /** 采样边投影缓存命中次数。 */
  private sampledEdgeProjectionHits = 0

  /** 实际世界到屏幕投影点次数。 */
  private projectedPointBuilds = 0

  /** 复用的顶点命中结果，避免每次 pointermove 创建结果对象。 */
  private readonly vertexHitResult = {
    vertexIndex: 0,
    distance: 0,
    point: this.bestVertexWorldPoint,
    screenDistance: 0,
    rayDistance: 0,
  }

  /** 复用的边命中结果，避免每次 pointermove 创建结果对象。 */
  private readonly edgeHitResult = {
    edgeIndex: 0,
    distance: 0,
    point: this.bestEdgeWorldPoint,
    screenDistance: 0,
    visibleHalfWidth: FEATURE_LINE_PICK_RADIUS_PX,
    segmentParameter: 0,
  }

  /**
   * 创建有界的多宿主拓扑投影缓存。
   *
   * @param options 宿主数量与投影点数量双预算。
   */
  public constructor(options: ImportedModelTopologySnapshotPickerOptions = {}) {
    this.maxCachedContexts = Math.max(
      1,
      Math.floor(options.maxCachedContexts ?? DEFAULT_MAX_CACHED_PROJECTION_CONTEXTS),
    )
    this.maxCachedProjectedPoints = Math.max(
      1,
      Math.floor(options.maxCachedProjectedPoints ?? DEFAULT_MAX_CACHED_PROJECTED_POINTS),
    )
  }

  /**
   * 从当前命中面的真实拓扑顶点中选择最近点。
   *
   * @param host 当前拓扑宿主。
   * @param faces 当前射线命中的面快照。
   * @param input 当前拓扑快照拾取上下文。
   * @param projectionIdentity 实例槽不可变身份；普通 Mesh 默认使用 host。
   * @returns 最近顶点命中；没有进入点拾取半径时返回 `null`。
   */
  public pickNearestVertex(
    host: FeatureResultMesh,
    faces: readonly TopologySnapshotFace[],
    input: ImportedModelTopologySnapshotPickContext,
    projectionIdentity: object = host,
  ): ImportedModelTopologyVertexSnapshotHit | null {
    const projectionContext = this.resolveProjectionContext(host, input, projectionIdentity)
    this.collectCandidateVertexIndices(host, faces)

    let bestVertexIndex: number | null = null
    let bestScreenDistance = Number.POSITIVE_INFINITY
    let bestRayDistance = Number.POSITIVE_INFINITY

    for (const vertexIndex of this.vertexIndices) {
      const vertex = host.getTopologyVertexSnapshotByIndex(vertexIndex)
      if (!vertex || vertex.role === 'curve-reference') {
        continue
      }

      // 同一相机、视口和宿主位姿下复用权威顶点投影，禁止为候选顶点创建 Sprite。
      const projection = this.resolveVertexProjection(projectionContext, vertex, host, input)
      if (!projection.valid) {
        continue
      }

      const deltaX = input.clientX - projection.clientX
      const deltaY = input.clientY - projection.clientY
      const screenDistance = Math.sqrt(deltaX * deltaX + deltaY * deltaY)
      if (screenDistance > this.vertexPickRadiusPx) {
        continue
      }

      const rayDistance = this.computeRayDistance(
        input.ray,
        projection.worldX,
        projection.worldY,
        projection.worldZ,
      )
      if (rayDistance < 0) {
        continue
      }

      if (
        screenDistance < bestScreenDistance
        || (
          Math.abs(screenDistance - bestScreenDistance) <= 1e-9
          && rayDistance < bestRayDistance
        )
      ) {
        bestVertexIndex = vertexIndex
        bestScreenDistance = screenDistance
        bestRayDistance = rayDistance
        this.bestVertexWorldPoint.set(
          projection.worldX,
          projection.worldY,
          projection.worldZ,
        )
      }
    }

    this.trimProjectionContexts(projectionContext)

    if (bestVertexIndex === null) {
      return null
    }

    this.vertexHitResult.vertexIndex = bestVertexIndex
    this.vertexHitResult.distance = bestRayDistance
    this.vertexHitResult.screenDistance = bestScreenDistance
    this.vertexHitResult.rayDistance = bestRayDistance
    return this.vertexHitResult
  }

  /**
   * 从当前命中面的真实拓扑边中选择最近边。
   *
   * @param host 当前拓扑宿主。
   * @param faces 当前射线命中的面快照。
   * @param input 当前拓扑快照拾取上下文。
   * @param projectionIdentity 实例槽不可变身份；普通 Mesh 默认使用 host。
   * @returns 最近边命中；没有进入边拾取半径时返回 `null`。
   */
  public pickNearestEdge(
    host: FeatureResultMesh,
    faces: readonly TopologySnapshotFace[],
    input: ImportedModelTopologySnapshotPickContext,
    projectionIdentity: object = host,
  ): ImportedModelTopologyEdgeSnapshotHit | null {
    const projectionContext = this.resolveProjectionContext(host, input, projectionIdentity)
    this.collectCandidateEdgeIndices(faces)

    let bestEdgeIndex: number | null = null
    let bestScreenDistance = Number.POSITIVE_INFINITY
    let bestRayDistance = Number.POSITIVE_INFINITY
    let bestSegmentParameter = 0

    for (const edgeIndex of this.edgeIndices) {
      const edge = host.getTopologyEdgeSnapshotByIndex(edgeIndex)
      if (!edge) {
        continue
      }

      const vertexA = host.getTopologyVertexSnapshotByIndex(edge.vertexAIndex)
      const vertexB = host.getTopologyVertexSnapshotByIndex(edge.vertexBIndex)
      if (!vertexA || !vertexB) {
        continue
      }

      const sampledPoints = edge.sampledPoints && edge.sampledPoints.length > 1
        ? edge.sampledPoints
        : null
      let pointData: Float64Array
      let pointCount: number
      if (sampledPoints) {
        const edgeProjection = this.resolveSampledEdgeProjection(
          projectionContext,
          edge,
          host,
          input,
        )
        pointData = edgeProjection.pointData
        pointCount = edgeProjection.pointCount
      } else {
        const startProjection = this.resolveVertexProjection(projectionContext, vertexA, host, input)
        const endProjection = this.resolveVertexProjection(projectionContext, vertexB, host, input)
        this.writeVertexProjectionToBuffer(startProjection, this.straightEdgePointData, 0)
        this.writeVertexProjectionToBuffer(
          endProjection,
          this.straightEdgePointData,
          PROJECTED_POINT_STRIDE,
        )
        pointData = this.straightEdgePointData
        pointCount = 2
      }

      // 曲线边按权威采样折线逐段检测，保持与 FeatureCurve 代理相同的拾取轮廓。
      for (let index = 0; index < pointCount - 1; index += 1) {
        const startOffset = index * PROJECTED_POINT_STRIDE
        const endOffset = startOffset + PROJECTED_POINT_STRIDE
        const startScreenX = pointData[startOffset + 3]!
        const startScreenY = pointData[startOffset + 4]!
        const endScreenX = pointData[endOffset + 3]!
        const endScreenY = pointData[endOffset + 4]!
        if (!Number.isFinite(startScreenX + startScreenY + endScreenX + endScreenY)) {
          continue
        }

        const segmentParameter = this.computeSegmentParameter(
          input.clientX,
          input.clientY,
          startScreenX,
          startScreenY,
          endScreenX,
          endScreenY,
        )
        const closestX = startScreenX + (endScreenX - startScreenX) * segmentParameter
        const closestY = startScreenY + (endScreenY - startScreenY) * segmentParameter
        const deltaX = input.clientX - closestX
        const deltaY = input.clientY - closestY
        const screenDistance = Math.sqrt(deltaX * deltaX + deltaY * deltaY)
        if (screenDistance > FEATURE_LINE_PICK_RADIUS_PX) {
          continue
        }

        const worldX = pointData[startOffset]!
          + (pointData[endOffset]! - pointData[startOffset]!) * segmentParameter
        const worldY = pointData[startOffset + 1]!
          + (pointData[endOffset + 1]! - pointData[startOffset + 1]!) * segmentParameter
        const worldZ = pointData[startOffset + 2]!
          + (pointData[endOffset + 2]! - pointData[startOffset + 2]!) * segmentParameter
        const rayDistance = this.computeRayDistance(input.ray, worldX, worldY, worldZ)
        if (rayDistance < 0) {
          continue
        }

        if (
          screenDistance < bestScreenDistance
          || (
            Math.abs(screenDistance - bestScreenDistance) <= 1e-9
            && rayDistance < bestRayDistance
          )
        ) {
          bestEdgeIndex = edgeIndex
          bestScreenDistance = screenDistance
          bestRayDistance = rayDistance
          bestSegmentParameter = segmentParameter
          this.bestEdgeWorldPoint.set(worldX, worldY, worldZ)
        }
      }
    }

    this.trimProjectionContexts(projectionContext)

    if (bestEdgeIndex === null) {
      return null
    }

    this.edgeHitResult.edgeIndex = bestEdgeIndex
    this.edgeHitResult.distance = bestRayDistance
    this.edgeHitResult.screenDistance = bestScreenDistance
    this.edgeHitResult.segmentParameter = bestSegmentParameter
    return this.edgeHitResult
  }

  /** @returns 只在显式性能检查时创建的投影缓存统计。 */
  public getStats(): ImportedModelTopologySnapshotPickerStats {
    return {
      viewportRefreshes: this.viewportRefreshes,
      contextRefreshes: this.contextRefreshes,
      contextHits: this.contextHits,
      contextEvictions: this.contextEvictions,
      vertexProjectionBuilds: this.vertexProjectionBuilds,
      vertexProjectionHits: this.vertexProjectionHits,
      sampledEdgeProjectionBuilds: this.sampledEdgeProjectionBuilds,
      sampledEdgeProjectionHits: this.sampledEdgeProjectionHits,
      projectedPointBuilds: this.projectedPointBuilds,
      cachedContexts: this.projectionContexts.size,
      cachedProjectedPoints: this.cachedProjectedPoints,
    }
  }

  /**
   * 清空派生投影及生命周期订阅，允许随后按新相机和新快照重新查询。
   *
   * 只归还借用引用，不销毁宿主、共享几何或材质；累计统计保留供窗口前后对照。
   */
  public clear(): void {
    for (const context of this.projectionContexts.keys()) {
      context.unsubscribeTopologyInvalidation()
    }
    this.projectionContexts.clear()
    this.projectionContextsByIdentity = new WeakMap()
    this.cachedProjectedPoints = 0
    this.viewportInitialized = false
    this.edgeIndices.clear()
    this.vertexIndices.clear()
  }

  /**
   * 解析当前宿主在相机和视口下的投影缓存上下文。
   *
   * @param host 当前拓扑宿主。
   * @param input 当前拾取上下文。
   * @param projectionIdentity 当前普通 Mesh 或实例槽的稳定身份。
   * @returns 可供本轮边点查询复用的宿主上下文。
   */
  private resolveProjectionContext(
    host: FeatureResultMesh,
    input: ImportedModelTopologySnapshotPickContext,
    projectionIdentity: object,
  ): ImportedModelTopologyProjectionHostContext {
    this.refreshViewportContext(input)
    const topologyRevision = host.getTopologyVisualRevision()
    let identityContexts = this.projectionContextsByIdentity.get(projectionIdentity)
    if (identityContexts) {
      for (let index = identityContexts.length - 1; index >= 0; index -= 1) {
        const context = identityContexts[index]!
        if (context.host !== host || context.topologyRevision !== topologyRevision) {
          this.removeProjectionContext(context)
          continue
        }
        if (this.areProjectionMatricesEqual(context.matrixWorld, host.matrixWorld)) {
          this.projectionContexts.delete(context)
          this.projectionContexts.set(context, context)
          this.contextHits += 1
          return context
        }
      }
    }

    identityContexts = this.projectionContextsByIdentity.get(projectionIdentity)
    if (!identityContexts) {
      identityContexts = []
      this.projectionContextsByIdentity.set(projectionIdentity, identityContexts)
    }
    const context: ImportedModelTopologyProjectionHostContext = {
      identity: projectionIdentity,
      host,
      // 宿主释放或替换快照时立即断开强引用，而不是等待下一次鼠标查询才能发现修订失效。
      unsubscribeTopologyInvalidation: host.onTopologyInvalidated(
        () => this.removeProjectionContext(context),
      ),
      topologyRevision,
      matrixWorld: host.matrixWorld.clone(),
      vertexProjections: new Map(),
      sampledEdgeProjections: new Map(),
      projectedPointCount: 0,
    }
    identityContexts.push(context)
    this.projectionContexts.set(context, context)
    this.contextRefreshes += 1
    return context
  }

  /**
   * 比较两个投影宿主矩阵，并仅合并矩阵乘法产生的不可见浮点抖动。
   *
   * @param left 已缓存矩阵。
   * @param right 当前宿主矩阵。
   * @returns 所有元素在相对容差内时返回 `true`。
   */
  private areProjectionMatricesEqual(left: THREE.Matrix4, right: THREE.Matrix4): boolean {
    const leftElements = left.elements
    const rightElements = right.elements
    for (let index = 0; index < 16; index += 1) {
      const leftValue = leftElements[index]!
      const rightValue = rightElements[index]!
      const scale = Math.max(1, Math.abs(leftValue), Math.abs(rightValue))
      if (Math.abs(leftValue - rightValue) > scale * PROJECTION_MATRIX_EQUALITY_EPSILON) {
        return false
      }
    }
    return true
  }

  /**
   * 相机矩阵或视口数值变化时清空全部屏幕投影。
   *
   * @param input 当前拾取上下文。
   */
  private refreshViewportContext(input: ImportedModelTopologySnapshotPickContext): void {
    const rect = input.containerRect
    const unchanged = this.viewportInitialized
      && this.cameraProjectionMatrix.equals(input.camera.projectionMatrix)
      && this.cameraWorldInverseMatrix.equals(input.camera.matrixWorldInverse)
      && this.viewportLeft === rect.left
      && this.viewportTop === rect.top
      && this.viewportWidth === rect.width
      && this.viewportHeight === rect.height
    if (unchanged) return

    this.clear()
    this.cameraProjectionMatrix.copy(input.camera.projectionMatrix)
    this.cameraWorldInverseMatrix.copy(input.camera.matrixWorldInverse)
    this.viewportLeft = rect.left
    this.viewportTop = rect.top
    this.viewportWidth = rect.width
    this.viewportHeight = rect.height
    this.viewportInitialized = true
    this.viewportRefreshes += 1
  }

  /**
   * 读取或构建单个权威顶点的世界和客户端投影。
   *
   * @param context 当前宿主投影上下文。
   * @param vertex 权威拓扑顶点。
   * @param host 当前拓扑宿主。
   * @param input 当前拾取上下文。
   * @returns 可复用投影条目。
   */
  private resolveVertexProjection(
    context: ImportedModelTopologyProjectionHostContext,
    vertex: TopologySnapshotVertex,
    host: FeatureResultMesh,
    input: ImportedModelTopologySnapshotPickContext,
  ): ImportedModelTopologyProjectedVertexCacheEntry {
    const cached = context.vertexProjections.get(vertex.index)
    if (cached?.vertex === vertex) {
      this.vertexProjectionHits += 1
      return cached
    }

    this.writeWorldPoint(vertex.position, host.matrixWorld, this.worldPoint)
    const valid = this.projectToClient(this.worldPoint, input, this.projectedPoint)
    const entry: ImportedModelTopologyProjectedVertexCacheEntry = {
      vertex,
      valid,
      worldX: this.worldPoint.x,
      worldY: this.worldPoint.y,
      worldZ: this.worldPoint.z,
      clientX: valid ? this.projectedPoint.x : Number.NaN,
      clientY: valid ? this.projectedPoint.y : Number.NaN,
    }
    if (!cached) {
      context.projectedPointCount += 1
      this.cachedProjectedPoints += 1
    }
    context.vertexProjections.set(vertex.index, entry)
    this.vertexProjectionBuilds += 1
    this.projectedPointBuilds += 1
    return entry
  }

  /**
   * 读取或构建一条采样曲线边的扁平投影。
   *
   * @param context 当前宿主投影上下文。
   * @param edge 权威拓扑边。
   * @param host 当前拓扑宿主。
   * @param input 当前拾取上下文。
   * @returns 可复用的采样点投影。
   */
  private resolveSampledEdgeProjection(
    context: ImportedModelTopologyProjectionHostContext,
    edge: TopologySnapshotEdge,
    host: FeatureResultMesh,
    input: ImportedModelTopologySnapshotPickContext,
  ): ImportedModelTopologyProjectedEdgeCacheEntry {
    const cached = context.sampledEdgeProjections.get(edge.index)
    if (cached?.edge === edge) {
      this.sampledEdgeProjectionHits += 1
      return cached
    }

    const sampledPoints = edge.sampledPoints ?? []
    const pointData = new Float64Array(sampledPoints.length * PROJECTED_POINT_STRIDE)
    for (let index = 0; index < sampledPoints.length; index += 1) {
      this.writeTopologyPointToBuffer(
        sampledPoints[index]!,
        host.matrixWorld,
        input,
        pointData,
        index * PROJECTED_POINT_STRIDE,
      )
    }
    if (cached) {
      context.projectedPointCount -= cached.pointCount
      this.cachedProjectedPoints -= cached.pointCount
    }
    const entry: ImportedModelTopologyProjectedEdgeCacheEntry = {
      edge,
      pointData,
      pointCount: sampledPoints.length,
    }
    context.sampledEdgeProjections.set(edge.index, entry)
    context.projectedPointCount += entry.pointCount
    this.cachedProjectedPoints += entry.pointCount
    this.sampledEdgeProjectionBuilds += 1
    this.projectedPointBuilds += entry.pointCount
    return entry
  }

  /**
   * 把拓扑局部点的世界和客户端投影写入扁平数组。
   *
   * @param point 拓扑局部点。
   * @param matrixWorld 宿主世界矩阵。
   * @param input 当前拾取上下文。
   * @param target 输出数组。
   * @param offset 当前点起始偏移。
   */
  private writeTopologyPointToBuffer(
    point: TopologyPoint3D,
    matrixWorld: THREE.Matrix4,
    input: ImportedModelTopologySnapshotPickContext,
    target: Float64Array,
    offset: number,
  ): void {
    this.writeWorldPoint(point, matrixWorld, this.worldPoint)
    const valid = this.projectToClient(this.worldPoint, input, this.projectedPoint)
    target[offset] = this.worldPoint.x
    target[offset + 1] = this.worldPoint.y
    target[offset + 2] = this.worldPoint.z
    target[offset + 3] = valid ? this.projectedPoint.x : Number.NaN
    target[offset + 4] = valid ? this.projectedPoint.y : Number.NaN
  }

  /**
   * 把顶点投影复制到本轮直线边复用缓冲。
   *
   * @param projection 已缓存顶点投影。
   * @param target 直线端点输出缓冲。
   * @param offset 当前端点起始偏移。
   */
  private writeVertexProjectionToBuffer(
    projection: ImportedModelTopologyProjectedVertexCacheEntry,
    target: Float64Array,
    offset: number,
  ): void {
    target[offset] = projection.worldX
    target[offset + 1] = projection.worldY
    target[offset + 2] = projection.worldZ
    target[offset + 3] = projection.valid ? projection.clientX : Number.NaN
    target[offset + 4] = projection.valid ? projection.clientY : Number.NaN
  }

  /**
   * 按宿主数量和投影点数量淘汰最久未使用的其它上下文。
   *
   * 当前正在拾取的宿主即使单体超过预算也保留到下一次上下文切换，避免一轮边/点仲裁内部抖动。
   *
   * @param activeContext 本轮正在使用的身份/矩阵上下文。
   */
  private trimProjectionContexts(activeContext: ImportedModelTopologyProjectionHostContext): void {
    while (
      this.projectionContexts.size > this.maxCachedContexts
      || this.cachedProjectedPoints > this.maxCachedProjectedPoints
    ) {
      let candidateContext: ImportedModelTopologyProjectionHostContext | null = null
      for (const context of this.projectionContexts.keys()) {
        if (context !== activeContext) {
          candidateContext = context
          break
        }
      }
      if (!candidateContext) return
      this.removeProjectionContext(candidateContext)
      this.contextEvictions += 1
    }
  }

  /**
   * 从 LRU、身份反向表和投影点预算中原子移除一个上下文。
   *
   * @param context 待移除上下文。
   */
  private removeProjectionContext(context: ImportedModelTopologyProjectionHostContext): void {
    if (!this.projectionContexts.delete(context)) return
    context.unsubscribeTopologyInvalidation()
    this.cachedProjectedPoints -= context.projectedPointCount
    const identityContexts = this.projectionContextsByIdentity.get(context.identity)
    if (!identityContexts) return
    const index = identityContexts.indexOf(context)
    if (index >= 0) identityContexts.splice(index, 1)
    if (identityContexts.length === 0) {
      this.projectionContextsByIdentity.delete(context.identity)
    }
  }

  /**
   * 收集当前候选面的去重边索引。
   *
   * @param faces 当前候选面快照。
   */
  private collectCandidateEdgeIndices(faces: readonly TopologySnapshotFace[]): void {
    this.edgeIndices.clear()
    for (const face of faces) {
      for (const edgeIndex of face.adjacentEdgeIndices) {
        this.edgeIndices.add(edgeIndex)
      }
    }
  }

  /**
   * 通过候选面的边端点收集真实拓扑顶点索引。
   *
   * @param host 当前拓扑宿主。
   * @param faces 当前候选面快照。
   */
  private collectCandidateVertexIndices(
    host: FeatureResultMesh,
    faces: readonly TopologySnapshotFace[],
  ): void {
    this.vertexIndices.clear()
    for (const face of faces) {
      for (const edgeIndex of face.adjacentEdgeIndices) {
        const edge = host.getTopologyEdgeSnapshotByIndex(edgeIndex)
        if (!edge) {
          continue
        }
        this.vertexIndices.add(edge.vertexAIndex)
        this.vertexIndices.add(edge.vertexBIndex)
      }
    }
  }

  /**
   * 把局部拓扑坐标写入世界坐标缓存。
   *
   * @param point 局部坐标元组。
   * @param matrixWorld 拓扑宿主世界矩阵。
   * @param target 输出向量。
   * @returns 输出向量。
   */
  private writeWorldPoint(
    point: TopologyPoint3D,
    matrixWorld: THREE.Matrix4,
    target: THREE.Vector3,
  ): THREE.Vector3 {
    return target.set(point[0], point[1], point[2]).applyMatrix4(matrixWorld)
  }

  /**
   * 把世界坐标投影到客户端像素坐标。
   *
   * @param worldPoint 世界坐标。
   * @param input 当前拾取输入。
   * @param target 投影结果缓存，x/y 为客户端坐标，z 为 NDC 深度。
   * @returns 投影是否有效且至少可能位于相机裁剪范围内。
   */
  private projectToClient(
    worldPoint: THREE.Vector3,
    input: ImportedModelTopologySnapshotPickContext,
    target: THREE.Vector3,
  ): boolean {
    target.copy(worldPoint).project(input.camera)
    if (!Number.isFinite(target.x) || !Number.isFinite(target.y) || !Number.isFinite(target.z)) {
      return false
    }
    if (target.z < -1 || target.z > 1) {
      return false
    }

    target.x = input.containerRect.left + ((target.x + 1) * 0.5) * input.containerRect.width
    target.y = input.containerRect.top + ((1 - target.y) * 0.5) * input.containerRect.height
    return true
  }

  /**
   * 计算世界点沿当前射线方向的距离。
   *
   * @param ray 当前射线。
   * @param pointX 世界坐标 X。
   * @param pointY 世界坐标 Y。
   * @param pointZ 世界坐标 Z。
   * @returns 沿射线方向的投影距离。
   */
  private computeRayDistance(
    ray: THREE.Ray,
    pointX: number,
    pointY: number,
    pointZ: number,
  ): number {
    return this.rayOffset.set(pointX, pointY, pointZ).sub(ray.origin).dot(ray.direction)
  }

  /**
   * 计算鼠标到屏幕线段最近点的参数。
   *
   * @param pointX 鼠标 X。
   * @param pointY 鼠标 Y。
   * @param startX 线段起点 X。
   * @param startY 线段起点 Y。
   * @param endX 线段终点 X。
   * @param endY 线段终点 Y。
   * @returns 限制在 `[0, 1]` 内的线段参数。
   */
  private computeSegmentParameter(
    pointX: number,
    pointY: number,
    startX: number,
    startY: number,
    endX: number,
    endY: number,
  ): number {
    const directionX = endX - startX
    const directionY = endY - startY
    const lengthSquared = directionX * directionX + directionY * directionY
    if (lengthSquared < 1e-12) {
      return 0
    }

    const offsetX = pointX - startX
    const offsetY = pointY - startY
    return THREE.MathUtils.clamp(
      (offsetX * directionX + offsetY * directionY) / lengthSquared,
      0,
      1,
    )
  }
}
